import { useId } from 'react';
import { Input, FormField } from '@amzn/gsce-shared-react-lib/components';

import { RichTextEditorField } from '../../../shared/ui/RichTextEditorField';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';

const ARIA_DESCRIBEDBY = 'aria-describedby';

export interface SubQuestionBasicFieldsProps {
  /** 1-based sub-question position — used for the field tracking ids. */
  readonly orderIndex: number;
  readonly title: string;
  readonly instruction: string;
  readonly onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onInstructionChange: (html: string) => void;
  /** Inline-error flags; each renders the field's error text + red state. */
  readonly titleError: boolean;
  readonly instructionError: boolean;
  readonly disabled: boolean;
  /** Hold the rich-text editor until the i18n bundle resolves (its placeholder
   *  is baked in at editor creation). */
  readonly isBundleLoading: boolean;
  readonly dataTestId?: string;
}

/**
 * The title + instruction fields of a sub-question row, each wrapped in the
 * shared {@link FormField} (label + required marker + inline error text) with
 * the shared tracked {@link Input} for the title and the rich-text editor for
 * the instruction. Extracted from {@link SubQuestionRow} to keep that file
 * within the line budget and to group the FormField/id boilerplate.
 */
export function SubQuestionBasicFields({
  orderIndex,
  title,
  instruction,
  onTitleChange,
  onInstructionChange,
  titleError,
  instructionError,
  disabled,
  isBundleLoading,
  dataTestId,
}: SubQuestionBasicFieldsProps): JSX.Element {
  const { t } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);
  const titleBaseId = useId();
  const instructionBaseId = useId();

  return (
    <>
      <FormField
        label={t('subTitleLabel')}
        required
        labelId={`${titleBaseId}-label`}
        htmlFor={`${titleBaseId}-control`}
        {...(titleError
          ? { error: t('subTitleRequiredError'), errorId: `${titleBaseId}-error` }
          : {})}
      >
        <Input
          id={`${titleBaseId}-control`}
          trackingId={`audit-whiz.review.sub-q.${orderIndex}.title`}
          value={title}
          onChange={onTitleChange}
          placeholder={t('subTitlePlaceholder')}
          disabled={disabled}
          labelId={`${titleBaseId}-label`}
          error={titleError}
          aria-invalid={titleError}
          {...(titleError ? { [ARIA_DESCRIBEDBY]: `${titleBaseId}-error` } : {})}
          {...(dataTestId ? { dataTestId: `${dataTestId}-title` } : {})}
        />
      </FormField>

      <FormField
        label={t('subInstructionLabel')}
        required
        labelId={`${instructionBaseId}-label`}
        {...(instructionError
          ? {
              error: t('subInstructionRequiredError'),
              errorId: `${instructionBaseId}-error`,
            }
          : {})}
      >
        {/* Stencil's RichTextEditor bakes its placeholder into the TipTap
         *  Placeholder extension at editor creation and never refreshes it, so
         *  hold the editor until the i18n bundle resolves. */}
        {isBundleLoading ? null : (
          <RichTextEditorField
            value={instruction}
            onChange={onInstructionChange}
            placeholder={t('subInstructionPlaceholder')}
            disabled={disabled}
            ariaLabel={t('subInstructionLabel')}
            invalid={instructionError}
            trackingId={`audit-whiz.review.sub-q.${orderIndex}.instruction`}
            {...(instructionError
              ? { ariaDescribedBy: `${instructionBaseId}-error` }
              : {})}
            {...(dataTestId !== undefined
              ? { dataTestId: `${dataTestId}-instruction` }
              : {})}
          />
        )}
      </FormField>
    </>
  );
}
